function readStoredValue(storage, key) {
    let raw;
    try {
        raw = storage.getItem(key);
    } catch (error) {
        throw new Error("RIOT_STORAGE_UNAVAILABLE");
    }
    if (raw === null || raw === undefined) {
        return null;
    }

    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (error) {
        throw new Error("RIOT_STORAGE_INVALID");
    }
    return parsed && parsed.data ? parsed.data : parsed;
}

function requiredText(value, errorCode) {
    if (typeof value !== "string" || !value.trim()) {
        throw new Error(errorCode);
    }
    return value.trim();
}

const RECORD_SESSION_PREFIX = "valorant-record:riot:session:";
const RECORD_CURRENT_KEY = "valorant-record:riot:current";
const RIOT_AUTO_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

function parseAccountSession(account, selectedPuuid, loginMode) {
    if (!account || typeof account !== "object" || Array.isArray(account)) {
        throw new Error("RIOT_SESSION_NOT_READY");
    }
    if (account.sub !== selectedPuuid) {
        throw new Error("RIOT_ACCOUNT_MISMATCH");
    }

    let accessToken = requiredText(account.access_token, "RIOT_SESSION_NOT_READY");
    try {
        accessToken = decodeURIComponent(accessToken);
    } catch (error) {
        throw new Error("RIOT_SESSION_INVALID");
    }

    return {
        puuid: selectedPuuid,
        accessToken,
        entitlementsToken: requiredText(account.entitlements_token, "RIOT_SESSION_NOT_READY"),
        loginMode,
        headersCookie: typeof account.HeadersCookie === "string" ? account.HeadersCookie.trim() : "",
    };
}

function parsePreferredSession(session, expectedPuuid) {
    if (!session || typeof session !== "object" || Array.isArray(session)) {
        return null;
    }
    const puuid = typeof session.puuid === "string" ? session.puuid.trim() : "";
    if (!puuid || (expectedPuuid && puuid !== expectedPuuid)) {
        return null;
    }
    try {
        return {
            puuid,
            accessToken: requiredText(session.accessToken, "RIOT_SESSION_NOT_READY"),
            entitlementsToken: requiredText(session.entitlementsToken, "RIOT_SESSION_NOT_READY"),
            loginMode: session.loginMode,
            headersCookie: typeof session.headersCookie === "string" ? session.headersCookie.trim() : "",
        };
    } catch (error) {
        return null;
    }
}

function readRiotAccountSession(storage, expectedPuuid, preferredSession) {
    const preferred = parsePreferredSession(preferredSession, expectedPuuid);
    if (preferred) {
        return preferred;
    }
    const targetStorage = storage || window.localStorage;
    const recordPuuid = expectedPuuid || readStoredValue(targetStorage, RECORD_CURRENT_KEY);
    if (recordPuuid) {
        const account = readStoredValue(targetStorage, RECORD_SESSION_PREFIX + recordPuuid);
        if (account) {
            return parseAccountSession(account, recordPuuid, account.loginMode);
        }
        throw new Error("RIOT_SESSION_NOT_READY");
    }
    throw new Error("RIOT_SESSION_NOT_READY");
}

function readLegacyShopAccountSession(storage, expectedPuuid) {
    if (typeof expectedPuuid !== "string" || !expectedPuuid.trim()) return null;
    const puuid = expectedPuuid.trim();
    let account;
    let storedMode;
    try {
        account = readStoredValue(storage, puuid);
        storedMode = readStoredValue(storage, "loginMode-" + puuid);
    } catch (error) {
        return null;
    }
    if (!account) return null;
    const loginMode = storedMode === "VShopMode" || storedMode === "ApiMode"
        ? storedMode
        : (account.loginMode === "VShopMode" ? "VShopMode" : "ApiMode");
    try {
        return parseAccountSession(account, puuid, loginMode);
    } catch (error) {
        return null;
    }
}

function saveRiotRecordSession(storage, session) {
    const targetStorage = storage || window.localStorage;
    const puuid = requiredText(session && session.puuid, "RIOT_SESSION_NOT_READY");
    const previous = readStoredValue(targetStorage, RECORD_SESSION_PREFIX + puuid);
    const incomingCookie = typeof (session && session.headersCookie) === "string"
        ? session.headersCookie.trim() : "";
    const account = {
        sub: puuid,
        access_token: requiredText(session && session.accessToken, "RIOT_SESSION_NOT_READY"),
        entitlements_token: requiredText(session && session.entitlementsToken, "RIOT_SESSION_NOT_READY"),
        loginMode: session.loginMode || "VShopMode",
        HeadersCookie: incomingCookie || (previous && previous.HeadersCookie) || "",
    };
    try {
        targetStorage.setItem(RECORD_SESSION_PREFIX + account.sub, JSON.stringify(account));
        targetStorage.setItem(RECORD_CURRENT_KEY, JSON.stringify(account.sub));
    } catch (error) {
        throw new Error("RIOT_STORAGE_UNAVAILABLE");
    }
}

function buildRiotBindRequest(gamerskyUserId, session, monitorTraceId) {
    if (!Number.isInteger(Number(gamerskyUserId)) || Number(gamerskyUserId) <= 0 || !session) {
        throw new Error("RIOT_BIND_REQUEST_INVALID");
    }
    const request = {
        gamerskyUserId: Number(gamerskyUserId),
        puuid: requiredText(session.puuid, "RIOT_SESSION_NOT_READY"),
        accessToken: requiredText(session.accessToken, "RIOT_SESSION_NOT_READY"),
        entitlementsToken: requiredText(session.entitlementsToken, "RIOT_SESSION_NOT_READY"),
    };
    if (monitorTraceId) request.monitorTraceId = monitorTraceId;
    return request;
}

function shouldAutoRefreshRiot(lastRefreshTime, now = Date.now()) {
    const refreshedAt = Number(lastRefreshTime);
    return Number.isFinite(refreshedAt)
        && refreshedAt > 0
        && now - refreshedAt > RIOT_AUTO_REFRESH_INTERVAL_MS;
}

function buildRiotQueryRequest(
    gamerskyUserId,
    bindInfoId,
    expectedPuuid,
    session,
    update,
    autoRefresh,
    queue,
    season,
    monitorTraceId
) {
    if (!Number.isInteger(Number(gamerskyUserId)) || Number(gamerskyUserId) <= 0) {
        throw new Error("RIOT_QUERY_REQUEST_INVALID");
    }
    const request = {
        gamerskyUserId: Number(gamerskyUserId),
        bindInfoId: bindInfoId || null,
        puuid: expectedPuuid || null,
        queue: queue || "all",
        historyLimit: 50,
        update: update === true || autoRefresh === true,
        autoRefresh: autoRefresh === true,
    };
    if (monitorTraceId) request.monitorTraceId = monitorTraceId;
    if (season) {
        request.season = season;
    }
    if (session) {
        if (expectedPuuid && session.puuid !== expectedPuuid) {
            throw new Error("RIOT_ACCOUNT_MISMATCH");
        }
        request.puuid = session.puuid;
        request.accessToken = requiredText(session.accessToken, "RIOT_SESSION_NOT_READY");
        request.entitlementsToken = requiredText(session.entitlementsToken, "RIOT_SESSION_NOT_READY");
    }
    return request;
}

module.exports = {
    buildRiotBindRequest,
    buildRiotQueryRequest,
    readLegacyShopAccountSession,
    readRiotAccountSession,
    saveRiotRecordSession,
    shouldAutoRefreshRiot,
};
